/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable import/prefer-default-export */
import { useMutation, UseMutationOptions, useQuery } from '@tanstack/react-query';
import Constants from 'Constants';
import {
  AdminFeedbackResponse,
  AdminLoginResponse,
  GenericApiResponse,
  GetDetailsResponse,
  makeRequest,
  ValidateCenterResponse,
} from 'services/api';
import { createKey, getAdminCommonHeaders, getSessionItem, setSessionItem } from 'utils/helper';

export const useValidateCenter = (
  options?: Omit<
    UseMutationOptions<
      GenericApiResponse<ValidateCenterResponse>,
      unknown,
      { programCode: string; password: string; adminCode: string },
      unknown
    >,
    'mutationFn'
  >,
) => {
  return useMutation({
    mutationKey: createKey(Constants.VALIDATE_CENTER_URL),
    mutationFn: async ({ programCode, password, adminCode }) => {
      const response = await makeRequest<ValidateCenterResponse>(Constants.VALIDATE_CENTER_URL, {
        programCode,
        password,
        adminCode,
      });
      return response;
    },
    retry: false,
    ...options,
  });
};

export const useAdminLogin = (
  options?: Omit<
    UseMutationOptions<
      GenericApiResponse<AdminLoginResponse>,
      unknown,
      { password: string; adminId: string },
      unknown
    >,
    'mutationFn'
  >,
) => {
  return useMutation({
    mutationKey: createKey(Constants.ADMIN_LOGIN_URL),
    mutationFn: async ({ adminId, password }: { password: string; adminId: string }) => {
      const response = await makeRequest<AdminLoginResponse>(Constants.ADMIN_LOGIN_URL, {
        adminId,
        password,
      });
      if (response.status === 200 && response.data.sessionId) {
        setSessionItem('adminSessionId', response.data.sessionId);
      }
      return response;
    },
    retry: false,
    ...options,
  });
};

export const useAdminLogout = (
  options?: Omit<
    UseMutationOptions<GenericApiResponse<string>, unknown, void, unknown>,
    'mutationFn'
  >,
) => {
  return useMutation({
    mutationKey: createKey(Constants.ADMIN_LOGOUT_URL),
    mutationFn: async () => {
      return makeRequest<string>(Constants.ADMIN_LOGOUT_URL, {}, getAdminCommonHeaders());
    },
    retry: false,
    ...options,
  });
};

export const useResetApp = (
  options?: Omit<
    UseMutationOptions<GenericApiResponse<string>, unknown, void, unknown>,
    'mutationFn'
  >,
) => {
  return useMutation({
    mutationKey: createKey(Constants.RESET_APP),
    mutationFn: async () => {
      const refreshToken = localStorage.getItem('refreshToken');
      return makeRequest<string>(Constants.RESET_APP, { refreshToken }, getAdminCommonHeaders());
    },
    retry: false,
    ...options,
  });
};

export const useSubmitAdminFeedback = (
  options?: Omit<
    UseMutationOptions<
      GenericApiResponse<AdminFeedbackResponse>,
      unknown,
      { phoneNumber: string; email: string; content: string },
      unknown
    >,
    'mutationFn'
  >,
) => {
  return useMutation({
    mutationKey: createKey(Constants.SUBMIT_ADMIN_FEEDBACK),
    mutationFn: async ({
      phoneNumber,
      email,
      content,
    }: {
      phoneNumber: string;
      email: string;
      content: string;
    }) => {
      const response = await makeRequest<AdminFeedbackResponse>(
        Constants.SUBMIT_ADMIN_FEEDBACK,
        {
          phoneNumber,
          email,
          content,
        },
        getAdminCommonHeaders(),
      );
      return response;
    },
    retry: false,
    ...options,
  });
};

export const useGetAdminDetails = () => {
  return useQuery({
    queryKey: createKey(Constants.GET_ADMIN_DETAILS),
    queryFn: () =>
      makeRequest<GetDetailsResponse>(Constants.GET_ADMIN_DETAILS, {}, getAdminCommonHeaders()),
    enabled: !!localStorage.getItem('adminId') && !!getSessionItem('adminSessionId'),
    select: (data) => data.data,
  });
};
